/**
 * HŘEBEN — a fictional ridge-top restaurant & wine bar in Brno. The demo
 * venue: a valid SiteDefinition + ContentSnapshot the REAL engine renders.
 *
 * Art direction lives in the injected preview stylesheet (venue-css.ts); this
 * file owns STRUCTURE + CONTENT only. The hero and about sections are
 * `freeform` (bespoke markup with data-edit markers + an inline SVG ridge
 * motif) so the hero is an art-directed composition, not the pattern hero's
 * eyebrow+centered+CTA+photo-band shape. Gallery is a `pattern` with a `list`
 * images field (editable add/remove/reorder); menu-card, hours and the contact
 * form are the real first-party modules.
 *
 * Every freeform template here passes the library's freeform policy
 * (checkFreeformTemplate + checkMarkerPlacement): no script/style/form, inline
 * SVG allowed, each data-edit marker present exactly once on a safe host, and
 * the root element carries id={anchor}.
 */
import type { ContentSnapshotT, SiteDefinitionT } from './engine.js';

/** The signature motif: stacked topographic ridge contours (thin strokes,
 *  drawn-on at load, parallaxed on scroll by the app). Pure geometry — no URL
 *  or active attributes, so it clears the freeform template policy. */
const RIDGE_SVG =
  '<svg class="hb-ridge" viewBox="0 0 1200 640" preserveAspectRatio="xMidYMax slice" aria-hidden="true" focusable="false">' +
  '<g fill="none" stroke="currentColor" stroke-width="1.25" vector-effect="non-scaling-stroke">' +
  '<path class="hb-ridge__l" style="--i:0" d="M-20 470 C 180 430 300 360 470 388 C 640 416 720 300 900 322 C 1040 340 1140 300 1220 316"/>' +
  '<path class="hb-ridge__l" style="--i:1" d="M-20 512 C 160 486 320 410 500 436 C 660 460 760 356 940 372 C 1060 384 1150 352 1220 366"/>' +
  '<path class="hb-ridge__l" style="--i:2" d="M-20 556 C 200 536 300 470 480 492 C 680 516 780 420 980 436 C 1080 444 1160 416 1220 428"/>' +
  '<path class="hb-ridge__l" style="--i:3" d="M-20 600 C 160 588 340 528 520 546 C 700 564 800 486 1000 500 C 1100 508 1170 484 1220 494"/>' +
  '<path class="hb-ridge__l" style="--i:4" d="M-20 640 C 220 632 320 588 540 602 C 740 616 840 552 1040 566 C 1120 572 1180 556 1220 564"/>' +
  '</g></svg>';

const HERO_HTML =
  '<section id="uvod" class="hb-hero hb-reveal">' +
  '<div class="hb-hero__inner">' +
  '<p class="hb-eyebrow hb-hero__eyebrow" data-edit="hero-eyebrow">Restaurace a vinný bar - Brno</p>' +
  '<h1 class="hb-hero__title" data-edit="hero-title">Sezónní kuchyně nad střechami města</h1>' +
  '<div class="hb-hero__lead">' +
  '<p class="hb-hero__sub" data-edit="hero-sub">Otevřená kuchyně, ohniště a sto vín ze střední Evropy - na hřebeni pod Špilberkem. Na večer si stůl raději rezervujte.</p>' +
  '<div class="hb-hero__cta">' +
  '<a class="hb-btn hb-btn--primary" href="#kontakt" data-edit="hero-cta">Rezervovat stůl</a>' +
  '<a class="hb-btn hb-btn--ghost" href="#menu">Prohlédnout menu</a>' +
  '</div>' +
  '</div>' +
  '<figure class="hb-hero__stage">' +
  '<span class="hb-hero__plate" aria-hidden="true"></span>' +
  '<img class="hb-hero__img" data-edit="hero-image" src="/assets/img/hero.webp" alt="Sál restaurace Hřeben s výhledem na město" width="1600" height="1067">' +
  RIDGE_SVG +
  '<figcaption class="hb-hero__cap"><span class="hb-hero__cap-dot" aria-hidden="true"></span>Večerní sál s výhledem na město</figcaption>' +
  '</figure>' +
  '<dl class="hb-hero__meta">' +
  '<div><dt>Otevřeno</dt><dd>út - ne, od 17:00</dd></div>' +
  '<div><dt>Kde</dt><dd>Špilberk, Brno</dd></div>' +
  '<div><dt>Od roku</dt><dd>2019</dd></div>' +
  '</dl>' +
  '</div>' +
  '</section>';

const ABOUT_HTML =
  '<section id="pribeh" class="hb-about hb-reveal">' +
  '<div class="hb-about__grid">' +
  '<div class="hb-about__col">' +
  '<p class="hb-eyebrow" data-edit="about-eyebrow">Náš příběh</p>' +
  '<p class="hb-about__lead" data-edit="about-lead">Vaříme z toho, co dozraje kousek od Brna. Menu měníme podle sezóny, občas podle toho, co ráno přivezou z trhu.</p>' +
  '<p class="hb-about__body" data-edit="about-body">Maso griluje na otevřeném ohni Tomáš, vína vybírá Klára. Chceme, aby jídlo chutnalo po kraji, ne po cestě přes půl světa. Přijďte na dlouhý večer, ne na rychlý oběd.</p>' +
  '<dl class="hb-about__stats">' +
  '<div><dt>od</dt><dd>2019</dd></div>' +
  '<div><dt>míst u stolů</dt><dd>40</dd></div>' +
  '<div><dt>vín na kartě</dt><dd>100+</dd></div>' +
  '</dl>' +
  '</div>' +
  '<figure class="hb-about__figure">' +
  '<img data-edit="about-image" src="/assets/img/about.webp" alt="Večerní interiér restaurace Hřeben" width="1200" height="900">' +
  '</figure>' +
  '</div>' +
  '</section>';

const FOOTER_HTML =
  '<footer id="pata" class="hb-footer">' +
  '<div class="hb-footer__grid">' +
  '<div class="hb-footer__brand"><span class="hb-footer__mark">Hřeben</span><p data-edit="footer-note">Restaurace a vinný bar nad Brnem. Rezervace na večer doporučujeme.</p></div>' +
  '<dl class="hb-footer__col"><dt>Adresa</dt><dd>Špilberk 210/1</dd><dd>602 00 Brno</dd></dl>' +
  '<dl class="hb-footer__col"><dt>Spojení</dt><dd>+420 543 210 019</dd><dd>ahoj@hreben.cz</dd></dl>' +
  '</div>' +
  '<div class="hb-footer__base"><span>© 2026 Hřeben</span><span class="hb-footer__credit">Web běží na <strong>Veldonu</strong></span></div>' +
  '</footer>';

export const definition: SiteDefinitionT = {
  contractVersion: 2,
  site: {
    name: 'Hřeben',
    locale: 'cs',
    domain: 'hreben.veldon.site',
    tagline: 'Sezónní kuchyně a vinný bar nad střechami Brna',
  },
  nav: {
    items: [
      { label: 'Úvod', target: '#uvod' },
      { label: 'Příběh', target: '#pribeh' },
      { label: 'Galerie', target: '#galerie' },
      { label: 'Menu', target: '#menu' },
      { label: 'Otevřeno', target: '#hodiny' },
      { label: 'Akce', target: '#akce' },
      { label: 'Obchod', target: '#obchod' },
      { label: 'Kontakt', target: '#kontakt' },
    ],
  },
  pages: [
    {
      id: 'home',
      path: '/',
      title: 'Hřeben - restaurace a vinný bar nad Brnem',
      metaDescription: 'Sezónní kuchyně z otevřeného ohně a sto vín ze střední Evropy na hřebeni pod Špilberkem.',
      sections: [
        {
          id: 'uvod',
          anchor: 'uvod',
          kind: 'freeform',
          html: HERO_HTML,
          editableManifest: [
            { fieldId: 'hero-eyebrow', type: 'text', label: 'Nadřádek' },
            { fieldId: 'hero-title', type: 'text', label: 'Hlavní nadpis' },
            { fieldId: 'hero-sub', type: 'text', label: 'Podtitulek' },
            { fieldId: 'hero-cta', type: 'text', label: 'Tlačítko' },
            { fieldId: 'hero-image', type: 'image', label: 'Hlavní fotka' },
          ],
        },
        {
          id: 'pribeh',
          anchor: 'pribeh',
          kind: 'freeform',
          html: ABOUT_HTML,
          editableManifest: [
            { fieldId: 'about-eyebrow', type: 'text', label: 'Nadřádek' },
            { fieldId: 'about-lead', type: 'text', label: 'Úvodní věta' },
            { fieldId: 'about-body', type: 'text', label: 'Text' },
            { fieldId: 'about-image', type: 'image', label: 'Fotka interiéru' },
          ],
        },
        {
          id: 'galerie',
          anchor: 'galerie',
          kind: 'pattern',
          type: 'gallery',
          variant: 'mosaic',
          fields: {
            heading: 'Ze sálu i z kuchyně',
            images: [
              { url: '/assets/img/gallery-1.webp', alt: 'Podávaný chod s vínem' },
              { url: '/assets/img/gallery-4.webp', alt: 'Víno u západu slunce nad kopci' },
              { url: '/assets/img/gallery-5.webp', alt: 'Večerní sál restaurace' },
              { url: '/assets/img/gallery-2.webp', alt: 'Maso z ohniště na prkně' },
              { url: '/assets/img/gallery-8.webp', alt: 'Terasa s výhledem' },
              { url: '/assets/img/gallery-3.webp', alt: 'Sezónní salát' },
            ],
          },
          editableManifest: [
            { fieldId: 'galerie-heading', type: 'text', label: 'Nadpis galerie', path: '/heading' },
            { fieldId: 'galerie-images', type: 'list', label: 'Fotky', path: '/images', constraints: { maxItems: 12 } },
          ],
        },
        { id: 'menu', anchor: 'menu', kind: 'module', moduleId: 'venue.menu-hours', instanceId: 'menu-1', view: 'menu-card' },
        { id: 'hodiny', anchor: 'hodiny', kind: 'module', moduleId: 'venue.menu-hours', instanceId: 'menu-1', view: 'hours' },
        { id: 'akce', anchor: 'akce', kind: 'module', moduleId: 'content.events', instanceId: 'events-1', view: 'cards', config: { locale: 'cs', heading: 'Co se u nás děje' } },
        { id: 'obchod', anchor: 'obchod', kind: 'module', moduleId: 'commerce.catalog', instanceId: 'shop-1', view: 'product-grid', config: { view: 'grid', locale: 'cs', columns: 3 } },
        {
          id: 'kontakt',
          anchor: 'kontakt',
          kind: 'module',
          moduleId: 'forms.contact',
          instanceId: 'contact-1',
          view: 'form',
          config: { formId: 'hreben-veldon-demo-contact-1' },
        },
        {
          id: 'pata',
          anchor: 'pata',
          kind: 'freeform',
          html: FOOTER_HTML,
          editableManifest: [{ fieldId: 'footer-note', type: 'text', label: 'Text v patičce' }],
        },
      ],
    },
  ],
};

export const snapshot: ContentSnapshotT = {
  contractVersion: 2,
  values: {
    uvod: {
      'hero-eyebrow': { type: 'text', value: 'Restaurace a vinný bar - Brno' },
      'hero-title': { type: 'text', value: 'Sezónní kuchyně nad střechami města' },
      'hero-sub': {
        type: 'text',
        value:
          'Otevřená kuchyně, ohniště a sto vín ze střední Evropy - na hřebeni pod Špilberkem. Na večer si stůl raději rezervujte.',
      },
      'hero-cta': { type: 'text', value: 'Rezervovat stůl' },
      'hero-image': { type: 'image', value: { src: '/assets/img/hero.webp', alt: 'Sál restaurace Hřeben s výhledem na město' } },
    },
    pribeh: {
      'about-eyebrow': { type: 'text', value: 'Náš příběh' },
      'about-lead': {
        type: 'text',
        value: 'Vaříme z toho, co dozraje kousek od Brna. Menu měníme podle sezóny, občas podle toho, co ráno přivezou z trhu.',
      },
      'about-body': {
        type: 'text',
        value:
          'Maso griluje na otevřeném ohni Tomáš, vína vybírá Klára. Chceme, aby jídlo chutnalo po kraji, ne po cestě přes půl světa. Přijďte na dlouhý večer, ne na rychlý oběd.',
      },
      'about-image': { type: 'image', value: { src: '/assets/img/about.webp', alt: 'Večerní interiér restaurace Hřeben' } },
    },
    galerie: {
      'galerie-heading': { type: 'text', value: 'Ze sálu i z kuchyně' },
      'galerie-images': {
        type: 'list',
        value: [
          { url: '/assets/img/gallery-1.webp', alt: 'Podávaný chod s vínem' },
          { url: '/assets/img/gallery-4.webp', alt: 'Víno u západu slunce nad kopci' },
          { url: '/assets/img/gallery-5.webp', alt: 'Večerní sál restaurace' },
          { url: '/assets/img/gallery-2.webp', alt: 'Maso z ohniště na prkně' },
          { url: '/assets/img/gallery-8.webp', alt: 'Terasa s výhledem' },
          { url: '/assets/img/gallery-3.webp', alt: 'Sezónní salát' },
        ],
      },
    },
    pata: {
      'footer-note': { type: 'text', value: 'Restaurace a vinný bar nad Brnem. Rezervace na večer doporučujeme.' },
    },
  },
  modules: {
    'menu-1': {
      dataVersion: 1,
      data: {
        menus: [
          {
            id: 'predkrmy',
            name: 'Předkrmy',
            items: [
              { id: 'chleba-sadlo', name: 'Kváskový chléb, škvarkové sádlo, cibule', price: '95 Kč' },
              { id: 'tatarak', name: 'Tatarák z hovězí svíčkové, topinky', description: 'Připravíme u stolu', price: '245 Kč' },
              { id: 'syr', name: 'Zrající sýry z Vysočiny, hruškové chutney', price: '185 Kč' },
            ],
          },
          {
            id: 'z-ohniste',
            name: 'Z ohniště',
            items: [
              { id: 'zebro', name: 'Pečené telecí žebro, kořenová zelenina', description: '8 hodin nad ohněm', price: '395 Kč' },
              { id: 'pstruh', name: 'Pstruh na grilu, máslo, citron, bylinky', price: '325 Kč' },
              { id: 'kachni-prsa', name: 'Kachní prsa, dýně, redukce z červeného vína', price: '365 Kč' },
              { id: 'houby', name: 'Grilované hlívy, polenta, pečený česnek', description: 'Vegetariánské', price: '265 Kč' },
            ],
          },
          {
            id: 'sladka-tecka',
            name: 'Sladká tečka',
            items: [
              { id: 'jablecny', name: 'Pečené jablko, skořice, zmrzlina ze zakysané smetany', price: '145 Kč' },
              { id: 'cokolada', name: 'Hořká čokoláda, višně, mandle', price: '155 Kč' },
            ],
          },
          {
            id: 'vinna-karta',
            name: 'Z vinné karty',
            items: [
              { id: 'ryzlink', name: 'Ryzlink rýnský, Mikulov', description: '0,15 l', price: '89 Kč' },
              { id: 'frankovka', name: 'Frankovka, Velké Bílovice', description: '0,15 l', price: '95 Kč' },
              { id: 'sekt', name: 'Sekt Petit, brut', description: '0,1 l', price: '110 Kč' },
            ],
          },
        ],
        hours: {
          regular: [
            { days: ['mon'], closed: true },
            { days: ['tue', 'wed', 'thu'], open: '17:00', close: '23:00' },
            { days: ['fri', 'sat'], open: '12:00', close: '00:00' },
            { days: ['sun'], open: '12:00', close: '22:00' },
          ],
          exceptions: [
            { date: '2026-12-24', closed: true, note: 'Štědrý den' },
            { date: '2026-12-31', open: '17:00', close: '02:00', note: 'Silvestr' },
          ],
        },
      },
    },
    'events-1': {
      dataVersion: 1,
      data: {
        events: [
          {
            id: 'vecer-moravskych-vin',
            title: 'Večer moravských vín',
            date: '2026-09-18',
            time: '19:00',
            description: 'Řízená degustace osmi vín od vinařů z jižní Moravy, k tomu sýry a chléb z pece.',
            image: { src: '/assets/img/gallery-4.webp', alt: 'Sklenice vína při západu slunce' },
            ctaLabel: 'Rezervovat místo',
            ctaUrl: '#kontakt',
          },
          {
            id: 'sklizova-vecere',
            title: 'Sklizňová večeře',
            date: '2026-10-02',
            time: '18:30',
            description: 'Sedmichodové menu z podzimní úrody, párované s víny. Jeden dlouhý stůl, jeden večer.',
            image: { src: '/assets/img/gallery-1.webp', alt: 'Podávaný chod s vínem' },
            ctaLabel: 'Událost na Facebooku',
            ctaUrl: 'https://www.facebook.com/hreben',
          },
          {
            id: 'zabijackove-hody',
            title: 'Zabíjačkové hody na terase',
            date: '2026-11-15',
            time: '12:00',
            description: 'Tradiční zabíjačka na terase, celý den, dokud nedojde. Přijďte s chutí.',
            image: { src: '/assets/img/gallery-8.webp', alt: 'Terasa s výhledem' },
          },
        ],
      },
    },
    'shop-1': {
      dataVersion: 1,
      data: {
        currency: 'CZK',
        products: [
          {
            id: 'ryzlink-rynsky-2022',
            name: 'Ryzlink rýnský 2022',
            description: 'Suché bílé z Mikulova. Láhev 0,75 l.',
            price: { amountMinor: 34900, currency: 'CZK' },
            image: { src: '/assets/img/gallery-4.webp', alt: 'Láhev vína Ryzlink' },
            available: true,
            kind: 'standard',
          },
          {
            id: 'degustacni-box',
            name: 'Degustační box',
            description: 'Tři vína a zrající sýry z Vysočiny, k vyzvednutí.',
            price: { amountMinor: 89000, currency: 'CZK' },
            image: { src: '/assets/img/gallery-1.webp', alt: 'Degustační box' },
            available: true,
            kind: 'standard',
          },
          {
            id: 'darkovy-poukaz',
            name: 'Dárkový poukaz',
            description: 'Hodnotu i vzkaz vybíráte vy. Platí dva roky.',
            price: { amountMinor: 50000, currency: 'CZK' },
            available: true,
            kind: 'gift_card',
            giftCard: { presetAmountsMinor: [50000, 100000, 200000] },
          },
        ],
      },
    },
    'contact-1': {
      dataVersion: 1,
      data: {
        heading: 'Rezervace a dotazy',
        intro: 'Napište nám, na kdy a pro kolik lidí chcete stůl. Ozveme se do pár hodin.',
        submitLabel: 'Odeslat poptávku',
        successMessage: 'Děkujeme, ozveme se vám co nejdříve.',
        errorMessage: 'Něco se nepovedlo, zkuste to prosím znovu.',
        consentText: 'Odesláním souhlasíte se zpracováním údajů pro vyřízení rezervace.',
      },
    },
  },
};
